/**
 * fehlerbericht, der Melder: Browserfehler gehen als Zeile an den Server.
 *
 * @description
 *   Bis zum 24.10.2026 gab es KEINEN Weg, auf dem ein Absturz im Browser zu uns
 *   gelangt: 0 Treffer fuer `window.onerror`, `unhandledrejection`, Sentry und
 *   `reportError`, und `reportWebVitals` sendet nichts. Anlass war ein Absturz im
 *   Standard-View auf dem Telefon des Owners (23.10.), der sich nicht
 *   nachstellen liess, er war schlicht nicht sichtbar.
 *
 *   Dieses Modul sammelt eine Meldung in einer Warteschlange in
 *   `sessionStorage` (hoechstens 5 Eintraege, die aeltesten fallen weg) und
 *   schickt sie an `POST /api/client-fehler`. Es schluckt JEDEN eigenen Fehler:
 *   ein Melder, der selbst wirft, erzeugt eine Meldeschleife.
 *
 *   🔴 **Der Sendeweg faehrt NICHT ueber den `window.fetch`-Mantel**
 *   (`index.js`). Der Mantel wartet vorher auf einen Token und ruft bei einem
 *   Fehlschlag `merkeFehlschlag`, die Meldung liefe mit ihrem EIGENEN Fehler in
 *   die Ausfallsonde (Rueckkopplung), und die 500-ms-Frist des Chunk-Pfads waere
 *   keine Frist, sondern eine Hoffnung. Dieselbe Begruendung steht in
 *   `api/serviceOutage.js` fuer die Sonde. Deshalb wird `window.fetch` beim
 *   LADEN dieses Moduls festgehalten, es ist der erste Import von `index.js`,
 *   also noch die unverpackte Referenz.
 *
 *   🔴 **Keine Auffrischung, keine 401-Wiederholung.** Der Token kommt aus der
 *   aktuellen Sitzung (`supabase.auth.getSession()` liest den gespeicherten
 *   Zustand). Ist keiner da, bleibt der Eintrag liegen und geht beim naechsten
 *   Laden hinaus, genau der Fall „Fehler zwischen Lauscher und Token".
 *
 *   🔴 **Der Chunk-Pfad reiht nur ein.** `lazyMitNeuladen` laedt unmittelbar nach
 *   dem Einreihen neu (`nurEinreihen: true`); gesendet wird nach dem Neuladen.
 *   Ein Timer vor `location.reload()` waere nicht durchsetzbar.
 *
 * @useWhen
 *   - Aus den zwei Fensterlauschern (`fehlerlauscher.js`), den Fehlergrenzen
 *     (`ErrorBoundary`, `design/BestandGrenze`) und `lazyMitNeuladen`
 *
 * @dontUseFor
 *   - Fachliche Fehlerzustaende einer Seite (die gehoeren in ihre Oberflaeche)
 *   - Alles, was ein Besucher ohne Anmeldung sieht, ohne Token geht nichts hinaus
 *
 * @related
 *   - `fehlerlauscher.js`, die zwei Fensterlauscher, erster Import von `index.js`
 *   - `components/common/ErrorBoundary.jsx`, meldet `boundary` bzw. `chunk`
 *   - `design/BestandGrenze.jsx`, meldet `grenze_bestand`
 *   - `lazyMitNeuladen.js`, meldet `chunk` und laedt dann neu
 *
 * @reuseHints
 *   - Jede kuenftige Fehlergrenze meldet ueber denselben Aufruf
 */

import { supabase } from '../api/authHelpers';

/** Schluessel der Warteschlange in `sessionStorage` (je Tab, ueberlebt das Neuladen). */
export const WARTESCHLANGE_SCHLUESSEL = 'pog.fehlerbericht.warteschlange';

/** Die Wertemenge von `art`, dieselbe wie in `backend/client_fehler.py`. */
export const ARTEN = ['boundary', 'grenze_bestand', 'onerror', 'rejection', 'chunk'];

/** Hoechstens so viele Meldungen je Seitenaufruf (Plan: 5). */
export const MAX_JE_SEITE = 5;

/** Hoechstens so viele Eintraege in der Warteschlange; die aeltesten fallen weg. */
export const MAX_WARTESCHLANGE = 5;

const MAX_TEXT = 1000;
const MAX_STAPEL = 8000;

const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const ZIEL = `${API_BASE}/api/client-fehler`;

// 🔴 Beim Laden festgehalten: dieses Modul ist der erste Import von `index.js`,
// also ist `window.fetch` hier noch die unverpackte Referenz. Spaeter zeigt
// `window.fetch` auf den Mantel, der zaehlt und auf einen Token wartet.
const _origFetch = typeof window !== 'undefined' && window.fetch
  ? window.fetch.bind(window)
  : null;

// Je Seitenaufruf, absichtlich NICHT in `sessionStorage`: ein Neuladen setzt die
// Zaehlung zurueck, und genau dann ist ein neuer Bericht richtig.
let gemeldet = 0;
const gesehen = new Set();
let laeuft = null;
let nachlauf = null;
let nachlaeufe = 0;

/**
 * Hoechstens so viele Nachlaeufe in Folge, bis eine Meldung angenommen wird oder
 * eine neue dazukommt. Der Abstand verdoppelt sich (2, 4, 8, 16, 32 s), danach
 * ruht der Melder: eine offene Seite ohne Sitzung schickt also nicht dauerhaft
 * alle 2 s einen Versuch (Regel-15-Tor W5, Punkt 4).
 */
export const MAX_NACHLAEUFE = 5;

// 🔴 **Warum es einen Nachlauf gibt, gemessen am 2026-10-24.** Ein Render-Absturz
// tritt ein bis zwei Sekunden nach dem Laden auf. `supabase.auth.getSession()`
// liefert in diesem Moment noch `null` (der Client ist am Initialisieren), die
// Meldung bliebe bis zum naechsten Neuladen liegen. Der Nachlauf ist KEIN
// Auffrischen: er liest dieselbe Sitzung 2 s spaeter erneut.
function planeNachlauf() {
  if (nachlauf || nachlaeufe >= MAX_NACHLAEUFE) return;
  const warte = 2000 * (2 ** nachlaeufe);
  nachlaeufe += 1;
  nachlauf = setTimeout(() => {
    nachlauf = null;
    sendeWarteschlange();
  }, warte);
}

function lesen() {
  try {
    const roh = window.sessionStorage.getItem(WARTESCHLANGE_SCHLUESSEL);
    const liste = roh ? JSON.parse(roh) : [];
    return Array.isArray(liste) ? liste.slice(-MAX_WARTESCHLANGE) : [];
  } catch {
    return [];
  }
}

function schreiben(liste) {
  try {
    window.sessionStorage.setItem(
      WARTESCHLANGE_SCHLUESSEL,
      JSON.stringify(liste.slice(-MAX_WARTESCHLANGE)),
    );
  } catch {
    // Ohne Speicher gibt es keine Warteschlange, die Meldung ist dann weg,
    // aber der Melder wirft nicht.
  }
}

function einreihen(eintrag) {
  const liste = lesen();
  liste.push(eintrag);
  schreiben(liste);
}

function istStandalone() {
  try {
    return Boolean(window.matchMedia && window.matchMedia('(display-mode: standalone)').matches);
  } catch {
    return false;
  }
}

function bundleName() {
  try {
    const treffer = performance.getEntriesByType('resource')
      .map((e) => e.name)
      .filter((n) => /\/main\.[^/]+\.js(\?|$)/.test(n));
    return treffer.length ? treffer[treffer.length - 1].split('/').pop() : '';
  } catch {
    return '';
  }
}

/**
 * Der Zugangstoken der aktuellen Sitzung.
 *
 * 🔴 **Zwei Wege, und der zweite ist gemessen noetig** (2026-10-24): auf einer
 * Seite, deren Baum gerade abgestuerzt ist, liefert `supabase.auth.getSession()`
 * `null`, obwohl die Sitzung im Speicher liegt (`sb-<ref>-auth-token`, Probe im
 * Playwright-Lauf). Dann wird derselbe Speicher gelesen, den die App selbst
 * ablegt, KEINE Auffrischung, kein Netzzugriff, keine zweite Sitzung.
 */
function tokenAusSpeicher() {
  try {
    for (let i = 0; i < window.localStorage.length; i += 1) {
      const schluessel = window.localStorage.key(i);
      if (!schluessel || !schluessel.endsWith('-auth-token')) continue;
      const sitzung = JSON.parse(window.localStorage.getItem(schluessel));
      if (sitzung && typeof sitzung.access_token === 'string' && sitzung.access_token) {
        return sitzung.access_token;
      }
    }
  } catch {
    // Kein lesbarer Speicher, dann gibt es keinen Token.
  }
  return null;
}

async function holeToken() {
  // 🔴 Der Speicher ZUERST: `getSession()` haengt auf einer abgestuerzten Seite
  // (gemessen 2026-10-24: die Zusage loest nie auf, der Melder wartete und die
  // Warteschlange blieb stehen). Der gespeicherte Token ist derselbe, den
  // `getSession()` zurueckgaebe, ohne Auffrischung, ohne Netzzugriff.
  const ausSpeicher = tokenAusSpeicher();
  if (ausSpeicher) return ausSpeicher;
  try {
    const { data } = await supabase.auth.getSession();
    return data?.session?.access_token || null;
  } catch {
    return null;
  }
}

/**
 * Schickt, was in der Warteschlange liegt. Nie werfen.
 *
 * @returns {Promise<void>} erledigt, wenn der Versuch durch ist
 */
export function sendeWarteschlange() {
  if (laeuft) return laeuft;
  // 🔴 **Der Zustand wird NACH der Zuweisung zurueckgesetzt, nicht im Rumpf**
  // (gemessen 2026-10-24). Der Rumpf lief bei leerer Warteschlange ohne ein
  // einziges `await` durch, sein `finally` setzte `laeuft = null`, und die
  // Zuweisung `laeuft = (async () => …)()` schrieb danach die bereits erledigte
  // Zusage zurueck. Jede spaetere Meldung bekam diese Zusage und sendete NIE.
  // Genau daran fielen die Proben (a) und (b): die Seite laedt, der Nachlauf
  // findet nichts, und der Absturz danach ging ins Leere. (c) ueberlebte nur,
  // weil das Neuladen den Modulzustand frisch macht.
  const lauf = (async () => {
    try {
      if (!_origFetch) return;
      let liste = lesen();
      if (!liste.length) return;
      const token = await holeToken();
      if (!token) {
        planeNachlauf();
        return;
      }
      for (const eintrag of [...liste]) {
        let antwort;
        try {
          antwort = await _origFetch(ZIEL, {
            method: 'POST',
            keepalive: true,
            cache: 'no-store',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(eintrag),
          });
        } catch {
          planeNachlauf();
          return; // Netzfehler: der Eintrag bleibt liegen.
        }
        if (antwort.ok) nachlaeufe = 0;
        if (antwort.ok || antwort.status === 400 || antwort.status === 413) {
          // Angenommen oder endgueltig abgelehnt, beides raus aus der Schlange.
          // 🔴 Aus der FRISCH gelesenen Schlange entfernen, nicht aus der
          // Momentaufnahme: sonst loescht das Schreiben eine Meldung mit, die
          // waehrend des Sendens dazukam (gemessen 2026-10-24: der Absturz selbst
          // kam als `boundary` dazu und wurde vom `onerror`-Lauf ueberschrieben).
          schreiben(lesen().filter((e) => e.kennung !== eintrag.kennung));
        } else if (antwort.status === 401) {
          // Kein Auffrischen, keine Wiederholung: beim naechsten Laden neu.
          return;
        }
      }
      // Was waehrend des Laufs dazukam, geht im Nachlauf hinaus.
      if (lesen().length) planeNachlauf();
    } catch {
      // Der Melder darf nie selbst werfen.
    }
  })();
  laeuft = lauf;
  lauf.finally(() => { if (laeuft === lauf) laeuft = null; });
  return lauf;
}

/**
 * Meldet einen Fehler. Reiht ihn ein und versucht zu senden.
 *
 * @param {object} p
 * @param {string} p.art - eine aus `ARTEN`
 * @param {unknown} p.fehler - Fehlerobjekt, Zeichenkette oder null
 * @param {string} [p.komponentenStapel] - `info.componentStack` einer Grenze
 * @param {boolean} [p.nurEinreihen] - nur in die Warteschlange (Chunk-Pfad)
 * @returns {Promise<void>|undefined} der Sendelauf, wenn einer gestartet wurde
 */
export function meldeFehler({ art, fehler, komponentenStapel, nurEinreihen = false } = {}) {
  try {
    if (!ARTEN.includes(art)) return undefined;
    if (gemeldet >= MAX_JE_SEITE) return undefined;

    const nachricht = String((fehler && fehler.message) || fehler || '').slice(0, MAX_TEXT);
    const stapel = String((fehler && fehler.stack) || '').slice(0, MAX_STAPEL);
    const schluessel = `${art}|${nachricht}|${stapel.split('\n')[0] || ''}`;
    if (gesehen.has(schluessel)) return undefined;
    gesehen.add(schluessel);
    gemeldet += 1;
    // Eine neue Meldung gibt dem Nachlauf wieder seine Versuche; begrenzt
    // bleibt es ueber `MAX_JE_SEITE`.
    nachlaeufe = 0;

    einreihen({
      // Nur fuer die Warteschlange: eine Meldung, die WAEHREND eines Sendelaufs
      // dazukommt, darf nicht von dessen Momentaufnahme ueberschrieben werden.
      kennung: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      art,
      nachricht,
      stapel,
      komponenten_stapel: String(komponentenStapel || '').slice(0, MAX_STAPEL),
      pfad: (window.location && window.location.pathname) || '',
      user_agent: (typeof navigator !== 'undefined' && navigator.userAgent) || '',
      fenster: `${window.innerWidth || 0}x${window.innerHeight || 0}`,
      standalone: istStandalone(),
      bundle: bundleName(),
    });

    return nurEinreihen ? undefined : sendeWarteschlange();
  } catch {
    return undefined;
  }
}
